import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatDistanceToNow } from 'date-fns';
import { MessageSquare, X, FileText, Image as ImageIcon, ListChecks, Layers } from 'lucide-react';
import type { ProjectHubUnreadListItem } from '../../utils/projectHubUnreadItems';

type ProjectHubUnreadPopoverProps = {
  open: boolean;
  /** Project context for the modal title (e.g. "#1234 Smith Residence"). */
  projectLabel?: string;
  items: ProjectHubUnreadListItem[];
  /** True while the project's comments/annotations are still streaming in. */
  loading?: boolean;
  onClose: () => void;
  onPick: (item: ProjectHubUnreadListItem) => void;
};

function locationIconFor(item: ProjectHubUnreadListItem): React.ReactNode {
  if (item.locationKind === 'group') return <Layers size={14} aria-hidden />;
  if (item.locationKind === 'task') return <ListChecks size={14} aria-hidden />;
  if (item.locationKind === 'image') return <ImageIcon size={14} aria-hidden />;
  return <FileText size={14} aria-hidden />;
}

function locationKindLabel(item: ProjectHubUnreadListItem): string {
  if (item.locationKind === 'group') return 'Group';
  if (item.locationKind === 'task') return 'Task';
  if (item.locationKind === 'image') return 'Image';
  return 'PDF';
}

/** Compact relative time like "3h ago" / "2d ago"; absolute date for older. */
function formatRelative(ms: number): string {
  if (!ms) return '';
  const now = Date.now();
  const delta = Math.max(0, now - ms);
  const dayMs = 24 * 60 * 60 * 1000;
  if (delta > 30 * dayMs) {
    return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return formatDistanceToNow(new Date(ms), { addSuffix: true });
}

/**
 * Centered modal listing unread comment locations for a project. Each row links
 * straight to the comment in its source surface (task thread, PDF pin, etc.).
 */
export const ProjectHubUnreadPopover: React.FC<ProjectHubUnreadPopoverProps> = ({
  open,
  projectLabel,
  items,
  loading,
  onClose,
  onPick,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="pm-unread-modal-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="pm-unread-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Unread comments for this project"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pm-unread-modal-header">
          <div className="pm-unread-modal-header-left">
            <span className="pm-unread-modal-icon" aria-hidden>
              <MessageSquare size={16} strokeWidth={2} />
            </span>
            <div className="pm-unread-modal-title-block">
              <span className="pm-unread-modal-eyebrow">Unread comments</span>
              {projectLabel && (
                <span className="pm-unread-modal-project">{projectLabel}</span>
              )}
            </div>
            {!loading && (
              <span className="pm-unread-modal-count" aria-label={`${items.length} unread`}>
                {items.length}
              </span>
            )}
          </div>
          <button
            type="button"
            className="pm-unread-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={16} aria-hidden />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="pm-unread-modal-empty">
            {loading ? 'Loading comments…' : 'No unread comments right now.'}
          </div>
        ) : (
          <ul className="pm-unread-modal-list">
            {items.map((item) => {
              const rel = formatRelative(item.timestampMs);
              const kind = locationKindLabel(item);
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    className="pm-unread-modal-item"
                    onClick={() => onPick(item)}
                  >
                    <span className="pm-unread-modal-item-icon" aria-hidden>
                      {locationIconFor(item)}
                    </span>
                    <span className="pm-unread-modal-item-body">
                      <span className="pm-unread-modal-item-top">
                        <span className="pm-unread-modal-item-primary">{item.primary}</span>
                        {rel && <span className="pm-unread-modal-item-time">{rel}</span>}
                      </span>
                      <span className="pm-unread-modal-item-meta">
                        {item.author && (
                          <>
                            <span className="pm-unread-modal-item-author">{item.author}</span>
                            <span className="pm-unread-modal-item-dot" aria-hidden>·</span>
                          </>
                        )}
                        <span className="pm-unread-modal-item-kind">{kind}</span>
                        <span className="pm-unread-modal-item-dot" aria-hidden>·</span>
                        <span className="pm-unread-modal-item-location" title={item.locationLabel}>
                          {item.locationLabel}
                        </span>
                        {item.locationDetail && (
                          <>
                            <span className="pm-unread-modal-item-dot" aria-hidden>·</span>
                            <span className="pm-unread-modal-item-detail">{item.locationDetail}</span>
                          </>
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>,
    document.body
  );
};
